import type { Theme as ThemeType } from '@/types';
import clsx from 'clsx';
import slugify from 'slugify';
import tinycolor from 'tinycolor2';

import { FontsLoader } from '../components/fonts-loader';

export interface ThemeProps {
  theme: Partial<ThemeType> | undefined;
  children?: React.ReactNode;
  /** used to set the scope of the css variables */
  container?: string;
}

/**
 * Applies a set of CSS variables based on the provided theme data.
 *
 * It dynamically generates CSS variables for colors, fonts,
 * and border radius to style components consistently throughout the application.
 *
 * This component ensures that all nested components within the container use the defined theme settings,
 * providing a consistent and customizable look and feel.
 */

export function Theme({ theme, children, container = 'root' }: ThemeProps) {
  if (!theme) {
    return <>{children}</>;
  }

  const { fonts, borderRadius, colors } = theme;

  // Generate font variables
  const headingFontVariable = fonts?.heading ? slugify(fonts.heading?.id || 'Marcellus', { lower: true }) : 'marcellus';
  const bodyFontVariable = fonts?.body ? slugify(fonts.body?.id || 'Josefin Sans', { lower: true }) : 'josefinSans';

  const styleEntries: string[] = [];
  const htmlStyleEntries: string[] = [];

  // Font variables
  if (headingFontVariable) {
    styleEntries.push(`--${headingFontVariable}: ${fonts?.heading?.id || 'Marcellus'};`);
  }
  if (bodyFontVariable) {
    styleEntries.push(`--${bodyFontVariable}: ${fonts?.body?.id || 'Josefin Sans'};`);
  }
  if (headingFontVariable) styleEntries.push(`--font-heading: var(--${headingFontVariable}), sans-serif;`);
  if (bodyFontVariable) styleEntries.push(`--font-body: var(--${bodyFontVariable}), sans-serif;`);

  // Font weight variables
  if (fonts?.headingWeight) {
    styleEntries.push(`--font-weight-heading: ${fonts?.headingWeight};`);
  } else {
    styleEntries.push(`--font-weight-heading: 700;`);
  }
  if (fonts?.bodyWeight) {
    styleEntries.push(`--font-weight-body: ${fonts?.bodyWeight};`);
  } else {
    styleEntries.push(`--font-weight-body: 400;`);
  }
  if (fonts?.headingCase) {
    styleEntries.push(`--text-transform-heading: ${fonts?.headingCase};`);
  }
  if (fonts?.bodyCase) {
    styleEntries.push(`--text-transform-body: ${fonts?.bodyCase};`);
  }

  // Font size and border radius variables
  if (fonts?.rootSize) htmlStyleEntries.push(`font-size: ${fonts?.rootSize}px;`);
  if (borderRadius !== undefined) styleEntries.push(`--radius: ${borderRadius}px;`);

  // Font styles
  styleEntries.push(`font-family: var(--font-body), sans-serif;`);
  styleEntries.push(`font-weight: var(--font-weight-body), sans-serif;`);

  // Color variables
  if (colors) {
    Object.entries(colors).forEach(([key, value]) => {
      if (value) {
        const variableName = `--${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
        styleEntries.push(`${variableName}: ${value};`);
      }
    });

    // Tonic-related variables
    if (colors.primary) {
      const primary = tinycolor(colors.primary);
      styleEntries.push(`--tonic-primary: ${primary.toString()};`);
      styleEntries.push(`--tonic-primaryLight: ${primary.clone().lighten(10).toString()};`);
      styleEntries.push(`--tonic-secondary: ${primary.toString()};`);
      styleEntries.push(`--tonic-secondaryLight: ${primary.clone().lighten(10).toString()};`);
    }
    if (colors.foreground) {
      const foreground = tinycolor(colors.foreground);
      styleEntries.push(`--tonic-shade1: ${foreground.toString()};`);
    }
    if (colors.muted) {
      const muted = tinycolor(colors.muted);
      styleEntries.push(`--tonic-shade3: ${muted.toString()};`);
    }
  }

  const style = `
  ${theme?.overrideLayout ? ':root,' : ''} #${container}, body {
    ${styleEntries.join('\n     ')}
  }

  html {
    ${htmlStyleEntries.join('\n     ')}
  }
  `;

  return (
    <div
      id={container}
      className={clsx(
        'text-foreground bg-background h-full w-full',
        tinycolor(colors?.background).isDark() && container === 'root' && 'dark',
        theme.classNames,
      )}
    >
      {children}

      <FontsLoader heading={fonts?.heading} body={fonts?.body} />
      <style id={`theme-${container}`} key={'theme'} dangerouslySetInnerHTML={{ __html: style }} />
    </div>
  );
}
